/**
 * The router over language-prefixed URLs (ADR-0062). The app sees a public page by its path without the prefix
 * (/ru/faq → "/faq"), so its routes, active links and page meta know one path per page; every link and navigation to a
 * public page gets the current language's prefix (`<Link href="/faq">` is /ru/faq on a Russian page), and app paths
 * (/accounts, /login, /programs/<version>) stay unprefixed.
 */
import { type ReactNode, useMemo } from "react";
import { Router } from "wouter";
import { useBrowserLocation } from "wouter/use-browser-location";
import { useLang } from "../i18n";
import { localizeHref, splitLocale } from "./publicRoutes";

type Nav = ReturnType<typeof useBrowserLocation>[1];

function useLocaleLocation(opts?: { ssrPath?: string }): [string, Nav] {
  const [raw, navigate] = useBrowserLocation(opts);
  const { lang } = useLang();
  return [splitLocale(raw).path, (to, options) => navigate(localizeHref(String(to), lang), options)];
}

/** `ssrPath`: the URL being prerendered; the browser reads its own location. */
export function LocaleRouter({ ssrPath, children }: { ssrPath?: string; children: ReactNode }) {
  const { lang } = useLang();
  const hrefs = useMemo(() => (href: string) => localizeHref(href, lang), [lang]);
  return (
    <Router hook={useLocaleLocation} ssrPath={ssrPath} hrefs={hrefs}>
      {children}
    </Router>
  );
}
